import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { OpeningHour } from '@/types/appConfig';
import { VISITE } from '@/data/marketing/content';

const KEY = ['public-opening-hours'] as const;

// Map nom du jour (français, sans accent, lowercase) → numéro JS
// (0=dimanche, 1=lundi, ..., 6=samedi).
const FR_DAY_TO_DOW: Record<string, number> = {
  dimanche: 0,
  lundi: 1,
  mardi: 2,
  mercredi: 3,
  jeudi: 4,
  vendredi: 5,
  samedi: 6,
};

const FR_DAY_NAMES = Object.keys(FR_DAY_TO_DOW);

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function isClosedHours(hours: string): boolean {
  return /ferm[eé]/i.test(hours.trim());
}

/**
 * Extrait les numéros de jours (0-6) couverts par une chaîne libre :
 *   "Mercredi"              → [3]
 *   "Lundi — Mardi"         → [1, 2]
 *   "Jeudi — Samedi"        → [4, 5, 6]
 *   "Lundi, Mardi, Vendredi"→ [1, 2, 5]
 * Une plage X — Y est étendue (wrap autour de la semaine si Y < X).
 */
function expandDayString(day: string): number[] {
  const norm = normalize(day);
  const matched: number[] = [];
  for (const name of FR_DAY_NAMES) {
    if (norm.includes(normalize(name))) {
      matched.push(FR_DAY_TO_DOW[name]);
    }
  }
  // Tri par ordre d'apparition dans la string (pas l'ordre alphabétique)
  matched.sort(
    (a, b) =>
      norm.indexOf(normalize(FR_DAY_NAMES[FR_DAY_NAMES.findIndex((n) => FR_DAY_TO_DOW[n] === a)])) -
      norm.indexOf(normalize(FR_DAY_NAMES[FR_DAY_NAMES.findIndex((n) => FR_DAY_TO_DOW[n] === b)]))
  );

  // Si pattern "X — Y" (séparateur tiret/em-dash OU mot " au ") et 2 noms
  // matchés → étend la plage en wrappant autour de la semaine.
  const hasRangeSep = /[—–-]|\bau\b/.test(norm);
  if (hasRangeSep && matched.length === 2) {
    const [a, b] = matched;
    const result: number[] = [];
    let cur = a;
    // Safety : limite à 7 itérations max au cas où
    for (let i = 0; i < 7; i++) {
      result.push(cur);
      if (cur === b) break;
      cur = (cur + 1) % 7;
    }
    return result;
  }
  return matched;
}

/**
 * Calcule les numéros de jours de la semaine où le bar est fermé,
 * basé sur les rows de opening_hours avec hours="Fermé". Les jours
 * non listés sont considérés OUVERTS (failsafe).
 */
export function getClosedDows(hours: OpeningHour[]): Set<number> {
  const closed = new Set<number>();
  for (const row of hours) {
    if (isClosedHours(row.hours)) {
      for (const dow of expandDayString(row.day)) {
        closed.add(dow);
      }
    }
  }
  return closed;
}

/** Hook dérivé : retourne Set des jours fermés (0-6). */
export function useClosedDows(): Set<number> {
  const { data: hours = VISITE.hours } = useOpeningHours();
  return useMemo(() => getClosedDows(hours), [hours]);
}

/** Parse "17h00", "17:00", "16h30" en minutes depuis minuit. */
function parseTimeToMinutes(s: string): number | null {
  const m = s.trim().match(/^(\d{1,2})[h:](\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (h < 0 || h > 24 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function minutesToHHMM(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Pour un jour donné (0=dim, 6=sam), retourne la fenêtre d'arrivée
 * recommandée pour réserver une table. min = ouverture, max =
 * fermeture - 60min (laisse 1h minimum au client). "00h00" en
 * fermeture = minuit (24:00). Null si bar fermé ce jour.
 */
export function getArrivalWindowForDow(
  dow: number,
  hours: OpeningHour[]
): { min: string; max: string; closeLabel: string } | null {
  for (const row of hours) {
    if (!expandDayString(row.day).includes(dow)) continue;
    if (isClosedHours(row.hours)) return null;
    const parts = row.hours.split(/[—–-]/).map((s) => s.trim());
    if (parts.length !== 2) continue;
    const openMin = parseTimeToMinutes(parts[0]);
    let closeMin = parseTimeToMinutes(parts[1]);
    if (openMin === null || closeMin === null) continue;
    if (closeMin === 0) closeMin = 24 * 60;
    const maxArrival = closeMin - 60;
    if (maxArrival <= openMin) continue;
    return {
      min: minutesToHHMM(openMin),
      max: minutesToHHMM(maxArrival),
      closeLabel: parts[1],
    };
  }
  return null;
}

/** Hook : fenêtre d'arrivée [min, max] pour la date sélectionnée. */
export function useArrivalWindow(
  date: Date | null
): { min: string; max: string; closeLabel: string } | null {
  const { data: hours = VISITE.hours } = useOpeningHours();
  return useMemo(() => {
    if (!date) return null;
    return getArrivalWindowForDow(date.getDay(), hours);
  }, [hours, date]);
}

/**
 * Lecture publique (anon + auth) des horaires d'ouverture via la vue
 * `public_opening_hours`. La vue expose UNIQUEMENT le champ
 * opening_hours d'app_config — pas les taux de paie ni la cash target.
 *
 * Fallback sur VISITE.hours (hardcodé) si la requête échoue (ex:
 * migration pas encore appliquée) — comme ça la page n'est jamais
 * cassée.
 */
export function useOpeningHours() {
  return useQuery({
    queryKey: KEY,
    staleTime: 5 * 60 * 1000, // 5 min — change rarement
    queryFn: async (): Promise<OpeningHour[]> => {
      const { data, error } = await supabase
        .from('public_opening_hours')
        .select('opening_hours')
        .maybeSingle();
      if (error) {
        if (import.meta.env.DEV) {
          console.warn('useOpeningHours fallback:', error.message);
        }
        return VISITE.hours;
      }
      const hours = (data?.opening_hours as OpeningHour[] | null) ?? null;
      return hours && hours.length > 0 ? hours : VISITE.hours;
    },
  });
}
